import { useTranslation } from 'react-i18next';
import {
  ADMISSION_TYPE_BOTTLE_SERVICE,
  ADMISSION_TYPE_GUEST_LIST,
  ADMISSION_TYPE_TICKET,
  AdmissionType,
} from '@domain/admission';
import { CreateAdmissionPresetRequest, Preset, PresetsRequest } from '@domain/preset';
import { useGenericMutation } from '@hooks/use-generic-mutation';
import { useToast } from '@hooks/use-toast/use-toast';
import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { request } from '@utils/request/request';
import { RequestMethod } from '@utils/request/request.type';

import { QUERY_KEYS } from './constants';

interface VariablesType {
  preset: CreateAdmissionPresetRequest;
  type: AdmissionType;
}

const updateQueryState = (queryClient: QueryClient, queryKey: string[], data: Preset, variables: VariablesType) => {
  const previousPresets = queryClient.getQueryData<PresetsRequest>(queryKey);

  if (previousPresets) {
    const presetsType = previousPresets[variables.type];
    const nextPresetsType = presetsType.map((preset) => (preset.id === data.id ? data : preset));

    queryClient.setQueryData<PresetsRequest>(queryKey, {
      ...previousPresets,
      [variables.type]: nextPresetsType,
    });
  }
};

const editPreset = async (admission: CreateAdmissionPresetRequest, type: AdmissionType, presetId: string) => {
  const urlType = {
    [ADMISSION_TYPE_GUEST_LIST]: 'guest-list-presets',
    [ADMISSION_TYPE_BOTTLE_SERVICE]: 'bottle-service-presets',
    [ADMISSION_TYPE_TICKET]: 'ticket-presets',
  };

  const url = `${import.meta.env.VITE_OFFERS_HOST}/v1/nightgraph/${urlType[type]}/${presetId}`;

  return request({ url, method: RequestMethod.PATCH, body: admission });
};

export const useEditAdmissionPreset = ({ floorplanId = '' }: { floorplanId?: string }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const toast = useToast();

  return useGenericMutation(
    [QUERY_KEYS.editAdmissionPreset],
    async ({
      preset,
      type,
      presetId,
    }: {
      preset: CreateAdmissionPresetRequest;
      type: AdmissionType;
      presetId: string;
    }) => editPreset(preset, type, presetId),
    {
      onSuccess: (data: Preset, variables) => {
        updateQueryState(queryClient, [QUERY_KEYS.getAdmissionsPresets], data, variables);
        updateQueryState(queryClient, ['get-floorplan-preset-admissions', floorplanId], data, variables);

        toast.push({
          title: t('offers:editOfferSuccessNotificationTitle'),
          text: '',
          type: 'success',
        });
      },
    }
  );
};
